import React from 'react';
import NextHead from 'next/head';
import { useRouter } from 'next/router';
import ProptTypes from 'prop-types';
import queryString from 'utils/queryString';
import { buildOrganizationJson } from 'components/rich';

import SETTINGS from '../../settings';

const HEAD_KEY = {
  CHAR_SET: 'charSet',
  HTTP_EQUIV_COMPATIBLE: 'httpEquivCompatible',
  HTTP_EQUIV_ACCEPT: 'httpEquivAccept',
  VIEWPORT: 'viewport',
  TITLE: 'title',
  DESCRIPTION: 'description',
  KEYWORDS: 'keywords',
  ICON: 'icon',
  ICON_APPLE: 'iconApple',
  ICON_16: 'icon16',
  ICON_32: 'icon32',
  ICON_MS: 'iconMS',
  MANIFEST: 'manifest',
  THEME_COLOR: 'themeColor',
  TWITTER_IMAGE: 'twitterImage',
  TWITTER_CARD: 'twitterCard',
  TWITTER_SITE: 'twitterSite',
  TWITTER_TITLE: 'twitterTitle',
  TWITTER_DESCRIPTION: 'twitterDescription',
  TWITTER_CREATOR: 'twitterCreator',
  OG_SITE: 'ogSite',
  OG_TITLE: 'ogTitle',
  OG_TYPE: 'ogType',
  OG_URL: 'ogUrl',
  OG_IMAGE: 'ogImage',
  OG_DESCRIPTION: 'ogDescription',
  CANONICAL: 'canonical',
  HERO_PRELOAD: 'heroPreload',
  ROBOTS: 'robots',
};

const ORGANIZATION = {
  name: 'MyCanvas',
  description:
    'MyCanvas turns family history and photos into art, books, and gifts made to last for generations.',
  logo: '/static/img/logo.png',
  email: 'help@mycanvas.com',
  sameAs: [
    SETTINGS.YOUTUBE_URL,
    SETTINGS.X_URL,
    SETTINGS.FACEBOOK_URL,
    SETTINGS.INSTAGRAM_URL,
    SETTINGS.PINTEREST_URL,
  ],
};

const Head = props => {
  const router = useRouter();

  const pageUrl = `${SETTINGS.HOST}${queryString.parseUrl(router.asPath).url}`;
  const canonicalUrl = props.canonicalPath ? `${SETTINGS.HOST}${props.canonicalPath}` : pageUrl;
  const organizationJson = buildOrganizationJson({
    ...ORGANIZATION,
    host: SETTINGS.HOST,
  });

  // Brand-first only where the audience searches for the brand itself (the
  // homepage, 100% brand queries in Search Console). Everywhere else the
  // page-specific part leads and the brand is the suffix. A fullTitle prop is
  // used as written.
  const fullTitle =
    props.fullTitle ??
    (props.brandFirst ? `${props.siteName}: ${props.title}` : `${props.title} | ${props.siteName}`);

  // Link unfurlers (Slack) show the escaped &amp; literally, so share titles spell it out.
  const shareTitle = fullTitle.replaceAll(' & ', ' and ');

  return (
    <NextHead>
      <meta key={HEAD_KEY.CHAR_SET} charSet="utf-8" />
      <meta key={HEAD_KEY.HTTP_EQUIV_COMPATIBLE} httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta
        key={HEAD_KEY.HTTP_EQUIV_ACCEPT}
        httpEquiv="Accept-CH"
        content="DPR, Viewport-Width, Width"
      />
      <meta
        key={HEAD_KEY.VIEWPORT}
        name="viewport"
        content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
      />

      {props.noindex && (
        <meta key={HEAD_KEY.ROBOTS} name="robots" content="noindex, follow" />
      )}

      <title key={HEAD_KEY.TITLE}>{fullTitle}</title>
      <meta key={HEAD_KEY.DESCRIPTION} name="description" content={props.description} />
      <meta key={HEAD_KEY.KEYWORDS} name="keywords" content={props.keywords} />
      <link key={HEAD_KEY.ICON} rel="icon" href="/favicon.ico" />
      <link
        key={HEAD_KEY.ICON_APPLE}
        rel="apple-touch-icon"
        sizes="180x180"
        href="/apple-touch-icon.png"
      />
      <link
        key={HEAD_KEY.ICON_32}
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/favicon-32x32.png"
      />
      <link
        key={HEAD_KEY.ICON_16}
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/favicon-16x16.png"
      />
      <meta key={HEAD_KEY.ICON_MS} name="msapplication-TileColor" content="#da532c" />
      <link key={HEAD_KEY.MANIFEST} rel="manifest" href="/site.webmanifest" />
      <meta key={HEAD_KEY.THEME_COLOR} name="theme-color" content="#ffffff" />
      <link key={HEAD_KEY.CANONICAL} rel="canonical" href={canonicalUrl} />

      {props.heroImage && (
        <link
          key={HEAD_KEY.HERO_PRELOAD}
          rel="preload"
          as="image"
          href={props.heroImage}
          type="image/webp"
          fetchPriority="high"
        />
      )}

      <meta key={HEAD_KEY.TWITTER_IMAGE} name="twitter:image" content={props.image} />
      <meta key={HEAD_KEY.TWITTER_CARD} name="twitter:card" content="summary_large_image" />
      <meta key={HEAD_KEY.TWITTER_SITE} name="twitter:site" content="@mycanvas" />
      <meta
        key={HEAD_KEY.TWITTER_TITLE}
        name="twitter:title"
        content={shareTitle}
      />
      <meta
        key={HEAD_KEY.TWITTER_DESCRIPTION}
        name="twitter:description"
        content={props.description}
      />

      <meta key={HEAD_KEY.OG_SITE} property="og:site_name" content={props.siteName} />
      <meta
        key={HEAD_KEY.OG_TITLE}
        property="og:title"
        content={shareTitle}
      />
      <meta key={HEAD_KEY.OG_TYPE} property="og:type" content={props.ogType || "website"} />
      <meta key={HEAD_KEY.OG_URL} property="og:url" content={canonicalUrl} />
      <meta key={HEAD_KEY.OG_IMAGE} property="og:image" content={props.image} />
      <meta key={HEAD_KEY.OG_DESCRIPTION} property="og:description" content={props.description} />

      <script
        key="ScriptMarkupOrganization"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: organizationJson }}
      />
    </NextHead>
  );
};

Head.propTypes = {
  siteName: ProptTypes.string,
  title: ProptTypes.string,
  // Whole <title> as written, without the site name.
  fullTitle: ProptTypes.string,
  // Path of another URL this page defers to, e.g. a thin sibling of a hub product.
  canonicalPath: ProptTypes.string,
  description: ProptTypes.string,
  keywords: ProptTypes.string,
  image: ProptTypes.string,
  ogType: ProptTypes.string,
  heroImage: ProptTypes.string,
  noindex: ProptTypes.bool,
  brandFirst: ProptTypes.bool,
};

Head.defaultProps = {
  siteName: 'MyCanvas',
  title: 'Beautiful photo books, calendars and posters',
  description:
    'Create and preserve your most precious memories. Custom calendars, collage posters, and photo books are easy to make with MyCanvas.',
  keywords: 'MyCanvas, Photo print, Photo book, Fine Art Prints, Print Instagram Photos',
  image:
    'https://parabo-production-nx-product-images.s3.amazonaws.com/categories/famhistorymc@2x.jpg',
};

export default Head;
